import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Upload, Plus, Edit, Star, Globe, Users, User, Image, Video, FileText } from 'lucide-react';
import { toast } from 'sonner';

function LoreContentManager({ token }) {
  const [loreContent, setLoreContent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newContent, setNewContent] = useState({
    title: '',
    content: '',
    lore_type: 'global',
    cohort_id: '',
    file_url: '',
    video_url: '',
    featured: false,
    author: 'admin',
    tags: ''
  });

  useEffect(() => {
    fetchLoreContent();
  }, []);

  const fetchLoreContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/lore-content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLoreContent(data.content || []);
      }
    } catch (error) {
      console.error('Failed to fetch LORE content');
    } finally {
      setLoading(false);
    }
  };

  const createContent = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/lore-content`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newContent,
          tags: newContent.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });

      if (!response.ok) throw new Error('Creation failed');

      toast.success('LORE content created successfully! 📚');
      setNewContent({
        title: '',
        content: '',
        lore_type: 'global',
        cohort_id: '',
        file_url: '',
        video_url: '',
        featured: false,
        author: 'admin',
        tags: ''
      });
      fetchLoreContent();
    } catch (error) {
      toast.error('Failed to create LORE content');
    }
  };

  const loreTypes = [
    { value: 'global', label: 'Global Library', icon: Globe, color: 'bg-blue-500', description: 'Available to all users' },
    { value: 'cohort', label: 'Cohort Specific', icon: Users, color: 'bg-green-500', description: 'Specific cohort only' },
    { value: 'private', label: 'Private Archive', icon: User, color: 'bg-purple-500', description: 'Individual user only' }
  ];

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold">📚 LORE Content Manager</h3>
          <p className="text-gray-600">Create wisdom library entries, upload files, and curate learning experiences</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 text-white hover:bg-indigo-700" style={{ borderRadius: 0 }}>
              <Plus className="w-4 h-4 mr-2" />
              Create LORE Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: 0 }}>
            <DialogHeader>
              <DialogTitle>Create New LORE Entry</DialogTitle>
            </DialogHeader>
            <form onSubmit={createContent} className="space-y-4">
              
              <div className="space-y-2">
                <label className="block font-bold">LORE Type</label>
                <div className="grid grid-cols-3 gap-3">
                  {loreTypes.map(type => {
                    const IconComponent = type.icon;
                    return (
                      <Button
                        key={type.value}
                        type="button"
                        onClick={() => setNewContent(prev => ({ ...prev, lore_type: type.value }))}
                        className={`p-4 ${newContent.lore_type === type.value ? type.color : 'bg-gray-100'} ${
                          newContent.lore_type === type.value ? 'text-white' : 'text-gray-700'
                        } hover:opacity-90`}
                        style={{ borderRadius: 0 }}
                      >
                        <div className="text-center">
                          <IconComponent className="w-6 h-6 mx-auto mb-1" />
                          <p className="text-sm font-bold">{type.label}</p>
                          <p className="text-xs">{type.description}</p>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>

              {newContent.lore_type === 'cohort' && (
                <div>
                  <label className="block font-bold mb-2">Cohort ID</label>
                  <Input
                    value={newContent.cohort_id}
                    onChange={(e) => setNewContent(prev => ({ ...prev, cohort_id: e.target.value }))}
                    placeholder="e.g., 2025-winter, 2025-spring"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              )}
              
              <div>
                <label className="block font-bold mb-2">Title</label>
                <Input
                  value={newContent.title}
                  onChange={(e) => setNewContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Title for this LORE entry"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>
              
              <div>
                <label className="block font-bold mb-2">Content</label>
                <Textarea
                  value={newContent.content}
                  onChange={(e) => setNewContent(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Write your wisdom, share insights, paste quotes, add reflections..."
                  className="border-black min-h-[200px]"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold mb-2">File URL (Optional)</label>
                  <div className="flex gap-2">
                    <Input
                      value={newContent.file_url}
                      onChange={(e) => setNewContent(prev => ({ ...prev, file_url: e.target.value }))}
                      placeholder="https://example.com/document.pdf"
                      className="border-black"
                      style={{ borderRadius: 0 }}
                    />
                    <Button
                      type="button"
                      className="bg-gray-600 text-white hover:bg-gray-700"
                      style={{ borderRadius: 0 }}
                      onClick={() => toast.info('File upload system ready for implementation')}
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="block font-bold mb-2">Video URL (Optional)</label>
                  <Input
                    value={newContent.video_url}
                    onChange={(e) => setNewContent(prev => ({ ...prev, video_url: e.target.value }))}
                    placeholder="https://youtube.com/watch?v=..."
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  />
                </div>
              </div>
              
              <div>
                <label className="block font-bold mb-2">Tags (comma-separated)</label>
                <Input
                  value={newContent.tags}
                  onChange={(e) => setNewContent(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="nvc, empathy, session1, wisdom"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                />
              </div>
              
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="featured"
                  checked={newContent.featured}
                  onChange={(e) => setNewContent(prev => ({ ...prev, featured: e.target.checked }))}
                />
                <label htmlFor="featured" className="font-normal">Featured Content (appears first)</label>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-indigo-600 text-white hover:bg-indigo-700 py-4"
                style={{ borderRadius: 0 }}
              >
                Create LORE Entry
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Content Overview */}
      <div className="grid md:grid-cols-3 gap-6">
        {loreTypes.map(type => {
          const IconComponent = type.icon;
          const typeContent = loreContent.filter(c => c.lore_type === type.value);
          return (
            <Card key={type.value} className={`border-2 ${type.color.replace('bg-', 'border-')}`} style={{ borderRadius: 0 }}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IconComponent className="w-5 h-5" />
                  {type.label}
                </CardTitle>
                <p className="text-sm text-gray-600">{type.description}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="text-2xl font-bold text-center">{typeContent.length}</div>
                  <div className="text-sm text-gray-600 text-center">
                    {typeContent.length === 1 ? 'Entry' : 'Entries'}
                  </div>
                  <Button 
                    className={`w-full ${type.color} text-white hover:opacity-90`}
                    style={{ borderRadius: 0 }}
                    onClick={() => {
                      setNewContent(prev => ({ ...prev, lore_type: type.value }));
                      toast.info(`Ready to create ${type.label} content!`);
                    }}
                  >
                    + Add {type.label}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent LORE Content */}
      <Card style={{ borderRadius: 0 }} className="border-2 border-indigo-500">
        <CardHeader>
          <CardTitle className="text-xl">Recent LORE Entries</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Loading LORE content...</p>
          ) : loreContent.length === 0 ? (
            <p className="text-center py-8 text-gray-500">
              No LORE content yet. Create your first entry to begin building the wisdom library!
            </p>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {loreContent.slice(0, 10).map((content) => {
                const typeConfig = loreTypes.find(t => t.value === content.lore_type);
                const IconComponent = typeConfig?.icon || Globe;
                
                return (
                  <Card key={content.id} className="border border-gray-300" style={{ borderRadius: 0 }}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded ${typeConfig?.color} text-white`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-800 flex items-center gap-2">
                                {content.title}
                                {content.featured && <Star className="w-4 h-4 text-yellow-500" />}
                              </h4>
                              <div className="flex gap-2">
                                <Badge className={typeConfig?.color}>{typeConfig?.label}</Badge>
                                {content.cohort_id && (
                                  <Badge variant="outline">Cohort: {content.cohort_id}</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 mb-3">{content.content}</p>
                          {(content.file_url || content.video_url) && (
                            <div className="flex gap-2">
                              {content.file_url && (
                                <Badge className="bg-blue-500 text-white">
                                  <FileText className="w-3 h-3 mr-1" />
                                  File
                                </Badge>
                              )}
                              {content.video_url && (
                                <Badge className="bg-red-500 text-white">
                                  <Video className="w-3 h-3 mr-1" />
                                  Video
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(content.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="border-4 border-yellow-500 bg-yellow-50" style={{ borderRadius: 0 }}>
        <CardHeader>
          <CardTitle className="text-xl text-yellow-800">🚀 Quick LORE Creation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <Button
              onClick={() => {
                setNewContent(prev => ({
                  ...prev,
                  lore_type: 'global',
                  title: 'Session Highlight',
                  content: 'Share a powerful moment or insight from today\'s session...'
                }));
                toast.success('Template ready for session highlight!');
              }}
              className="bg-blue-600 text-white hover:bg-blue-700 py-6"
              style={{ borderRadius: 0 }}
            >
              ✨ Session Highlight
            </Button>
            <Button
              onClick={() => {
                setNewContent(prev => ({
                  ...prev,
                  lore_type: 'global',
                  title: 'NVC Quote',
                  content: 'Add a meaningful quote from Marshall Rosenberg, LaShelle Charde, or other NVC teachers...'
                }));
                toast.success('Template ready for wisdom quote!');
              }}
              className="bg-purple-600 text-white hover:bg-purple-700 py-6"
              style={{ borderRadius: 0 }}
            >
              💬 Wisdom Quote
            </Button>
            <Button
              onClick={() => {
                setNewContent(prev => ({
                  ...prev,
                  lore_type: 'cohort',
                  title: 'Cohort Reflection',
                  content: 'Share collective insights from this learning community...'
                }));
                toast.success('Template ready for cohort reflection!');
              }}
              className="bg-green-600 text-white hover:bg-green-700 py-6"
              style={{ borderRadius: 0 }}
            >
              🤝 Cohort Wisdom
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}

export default LoreContentManager;